// src/Pages/finanzas-clientes/useFinanzasTab.ts
import { useCallback, useEffect, useState } from "react";
import { getMsg } from "../../utils/antdStatic";
import dayjs, { Dayjs } from "dayjs";
import { apiFetch } from "../../services/apiClient";
import { BookingRaw } from "./useArriendosSection";
import { ExpenseRow } from "./useExpensesSection";

export type FinanzasLineItem = {
  id: string;
  descripcion: string;
  monto: number;
  fecha: string;
};

const toNumber = (n: any): number => {
  const x = typeof n === "string" ? Number(n) : n;
  return Number.isFinite(x) ? Number(x) : 0;
};

const formatDayMonth = (date: string | null): string => {
  if (!date) return "";
  const d = dayjs(date);
  return d.isValid() ? d.format("DD/MM") : "";
};

function arriendoToLineItem(b: BookingRaw): FinanzasLineItem {
  const checkin = formatDayMonth(b.ingreso);
  const checkout = formatDayMonth(b.salida);
  return {
    id: `arr-${b.id}`,
    descripcion: `Arriendo ${checkin}-${checkout}`,
    monto: toNumber(b.reserva_total_propietario),
    fecha: b.ingreso || "",
  };
}

function gastoToLineItem(row: ExpenseRow): FinanzasLineItem {
  const fecha = formatDayMonth(row.fecha);
  const desc =
    row.descripcion?.trim() || `${row.categoria} ${fecha}`;
  return {
    id: `gasto-${row.id}`,
    descripcion: desc,
    monto: row.monto,
    fecha: row.fecha || "",
  };
}

export function useFinanzasTab(
  clientId: number | null | undefined,
  selectedPropId: number | null,
  month: Dayjs | null,
) {
  const [ingresos, setIngresos] = useState<FinanzasLineItem[]>([]);
  const [gastos, setGastos] = useState<FinanzasLineItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!selectedPropId || !month) return;

    try {
      setLoading(true);

      const monthStr = month.format("YYYY-MM");
      const from = month.startOf("month").toISOString();
      const to = month.endOf("month").toISOString();

      // Arriendos no necesita clientId, gastos sí
      const arriendosPromise = apiFetch<any>(
        `/finanzas/arriendos?propId=${selectedPropId}&month=${monthStr}`,
      );

      const gastosPromise = clientId
        ? apiFetch<{ data: ExpenseRow[] }>(
            `/finanzas/gastos/${clientId}?propId=${selectedPropId}&from=${from}&to=${to}&size=1000`,
          )
        : Promise.resolve(null);

      const [arriendosJson, gastosJson] = await Promise.all([
        arriendosPromise,
        gastosPromise,
      ]);

      if (!arriendosJson) throw new Error("Error cargando arriendos");

      let rawGastos: ExpenseRow[] = [];
      if (gastosJson) {
        rawGastos = gastosJson.data || [];
      }

      // Parse arriendos
      const rawArriendos: BookingRaw[] = Array.isArray(arriendosJson)
        ? arriendosJson
        : arriendosJson.rows || arriendosJson.data || [];

      // Income: arriendos (totalPropietario) + gastos donde el Cliente recibe
      // (`pagado_a.tipo === 1`). Antes se comparaba contra el string "1" — los
      // campos son JSONB `{tipo, nombre, ...}`, así que el filtro nunca matcheaba
      // y los "otros cargos" jamás aparecían acá. Fix de bug visible en
      // finanzas-cliente cuando el cliente RECIBÍA un cargo y no se contaba
      // como ingreso. Ver docs/flujo-financiero.md §10.5.
      const ingresosFromArriendos = rawArriendos.map(arriendoToLineItem);
      const ingresosFromGastos = rawGastos
        .filter((g) => g.pagado_a?.tipo === 1)
        .map(gastoToLineItem);

      // Egresos: gastos donde el Cliente paga (`pagado_por.tipo === 1`).
      const gastosItems = rawGastos
        .filter((g) => g.pagado_por?.tipo === 1)
        .map(gastoToLineItem);

      // Cronológico: lo más antiguo primero. La fecha de un arriendo es su
      // check-in (no la salida ni el id).
      const porFecha = (a: FinanzasLineItem, b: FinanzasLineItem) =>
        (a.fecha || "").slice(0, 10).localeCompare((b.fecha || "").slice(0, 10));

      setIngresos([...ingresosFromArriendos, ...ingresosFromGastos].sort(porFecha));
      setGastos([...gastosItems].sort(porFecha));
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error cargando finanzas");
      setIngresos([]);
      setGastos([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedPropId, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const subtotalIngresos = ingresos.reduce((acc, r) => acc + r.monto, 0);
  const subtotalGastos = gastos.reduce((acc, r) => acc + r.monto, 0);
  const total = subtotalIngresos - subtotalGastos;

  return {
    ingresos,
    gastos,
    subtotalIngresos,
    subtotalGastos,
    total,
    loading,
    refetch: fetchData,
  };
}
